import React from 'react';
import { EditIcon, PhoneIcon, MailIcon, MapPinIcon, StickyNoteIcon, HashIcon, Trash2Icon, UserPlusIcon } from 'lucide-react';
import { motion } from 'framer-motion';
export interface Customer {
  id: string;
  customerId: string;
  name: string;
  address: string;
  phone: string;
  email?: string;
  notes: string;
  secondaryContactName?: string;
  secondaryContactPhone?: string;
}
interface CustomerCardProps {
  customer: Customer;
  index: number;
  onEdit: (customer: Customer) => void;
  onDelete: (customer: Customer) => void;
  onViewDetail: (customerId: string) => void;
  readOnly?: boolean;
}
export function CustomerCard({
  customer,
  index,
  onEdit,
  onDelete,
  onViewDetail,
  readOnly = false
}: CustomerCardProps) {
  return <motion.article initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.3,
    delay: index * 0.05
  }} className="bg-white rounded-xl p-5 shadow-sm border border-stone-100 hover:shadow-md transition-shadow duration-200" data-id="element-208" data-mp="twfjyzbnqgg4">
      <div className="flex items-start justify-between mb-4" data-id="element-209" data-mp="29b2218dgxo3j">
        <div data-id="element-210" data-mp="29doglrys13v9">
          <h3 className="text-lg font-semibold text-stone-900 cursor-pointer hover:text-teal-600 transition-colors" onClick={() => onViewDetail(customer.id)} data-id="element-211" data-mp="2eb7ugqrkbemo">
            {customer.name}
          </h3>
          <div className="flex items-center gap-1.5 mt-1" data-id="element-212" data-mp="2nauy7j7rkwiw">
            <HashIcon className="w-3.5 h-3.5 text-teal-600" data-id="element-213" data-mp="2sag0a2i8dkzu" />
            <span className="text-sm font-medium text-teal-600" data-id="element-214" data-mp="2s8wked0hlbyl">
              {customer.customerId}
            </span>
          </div>
        </div>
        {!readOnly && <div className="flex items-center gap-1" data-id="element-215" data-mp="31z7mdu0m1b2u">
            <button onClick={() => onEdit(customer)} className="p-1.5 text-stone-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-all" aria-label="Rediger kunde" data-id="element-216" data-mp="36zlxxm1f735m">
              <EditIcon className="w-4 h-4" data-id="element-217" data-mp="1bxih5j0ekv6v" />
            </button>
            <button onClick={() => onDelete(customer)} className="p-1.5 text-stone-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all" aria-label="Slet kunde" data-id="element-218" data-mp="16xcnjxakoym5">
              <Trash2Icon className="w-4 h-4" data-id="element-219" data-mp="1fxsp317r9vod" />
            </button>
          </div>}
      </div>

      <div className="space-y-3" data-id="element-220" data-mp="1ulvti665gzb2">
        {customer.address && <div className="flex items-start gap-3" data-id="element-221" data-mp="1po41lc5i9d6v">
            <MapPinIcon className="w-4 h-4 text-teal-600 mt-0.5 flex-shrink-0" data-id="element-222" data-mp="1pnc4fww9f7eh" />
            <div data-id="element-223" data-mp="23mlm710469kk">
              <p className="text-sm text-stone-500" data-id="element-224" data-mp="23n3ghs37udpu">Adresse</p>
              <p className="text-stone-900 text-sm" data-id="element-225" data-mp="1zlrxi5eukqqm">{customer.address}</p>
            </div>
          </div>}

        {customer.phone && <div className="flex items-start gap-3" data-id="element-226" data-mp="99vos9ajei8f">
            <PhoneIcon className="w-4 h-4 text-teal-600 mt-0.5 flex-shrink-0" data-id="element-227" data-mp="58sctxvm16ch" />
            <div data-id="element-228" data-mp="3r9pys8w38a26">
              <p className="text-sm text-stone-500" data-id="element-229" data-mp="3r97s0ibsudg0">Telefon</p>
              <p className="text-stone-900 font-medium" data-id="element-230" data-mp="3rbu1wom5x51e">{customer.phone}</p>
            </div>
          </div>}

        {customer.email && <div className="flex items-start gap-3" data-id="element-231" data-mp="3hilmjgxsr3b1">
            <MailIcon className="w-4 h-4 text-teal-600 mt-0.5 flex-shrink-0" data-id="element-232" data-mp="3mirg52nmmzvr" />
            <div data-id="element-233" data-mp="3mgn23rw0kkhr">
              <p className="text-sm text-stone-500" data-id="element-234" data-mp="37lgux1l6mfmu">Email</p>
              <p className="text-stone-900 text-sm" data-id="element-235" data-mp="37jxenazj04l2">{customer.email}</p>
            </div>
          </div>}

        {customer.secondaryContactName && <div className="flex items-start gap-3" data-id="element-236" data-mp="2ylbytnlfv3nz">
            <UserPlusIcon className="w-4 h-4 text-teal-600 mt-0.5 flex-shrink-0" data-id="element-237" data-mp="2ymn1tl7287e1" />
            <div data-id="element-238" data-mp="33kf5tdkzbjyl">
              <p className="text-sm text-stone-500" data-id="element-239" data-mp="33lzoa4u0rcg3">Ekstra kontakt</p>
              <p className="text-stone-900 text-sm" data-id="element-240" data-mp="wuqs8h4gd7lw">
                {customer.secondaryContactName}
              </p>
              {customer.secondaryContactPhone && <p className="text-stone-500 text-xs" data-id="element-241" data-mp="2xw6r60udxmaz">
                  {customer.secondaryContactPhone}
                </p>}
            </div>
          </div>}

        {customer.notes && <div className="flex items-start gap-3" data-id="element-242" data-mp="2nwplvkj1pafj">
            <StickyNoteIcon className="w-4 h-4 text-teal-600 mt-0.5 flex-shrink-0" data-id="element-243" data-mp="2nzbulnho9vzc" />
            <div data-id="element-244" data-mp="2jvm60y0zzeeb">
              <p className="text-sm text-stone-500" data-id="element-245" data-mp="2jy8j1d6nqm3x">Noter</p>
              <p className="text-stone-900 text-sm line-clamp-2" data-id="element-246" data-mp="2eydp10rl5gen">
                {customer.notes}
              </p>
            </div>
          </div>}
      </div>
    </motion.article>;
}